<template>
  <div class="mx-auto rounded py-6 bg-white border">
    <portal-collect-payment-success
      v-if="showPaymentPaid"
      :payment-amount="amountJustPaid"
      @make-another-payment="makeAnotherPayment"
      :invoice="invoice"
      :invoice-updated="invoiceUpdated"
      :show-loader="!invoiceUpdated && invoice.allow_partial_payments"
      :selected-payment-method="selectedPaymentMethod"
    />
    <template v-else>
      <div
        class="px-8 pb-4 border-b border-gray-300"
        :class="{ 'mb-6': !invoice.allow_partial_payments }"
      >
        <div class="flex justify-between items-center">
          <div class="flex flex-col">
            <span class="text-xs text-gray-500">Balance due</span>
            <div class="flex items-center gap-3">
              <span class="text-3xl font-bold text-gray-800">{{ maxPaymentAmountFormatted }}</span>
              <portal-invoice-status-pill
                on-payment-page
                :invoice="invoice"
                :status-name="invoice?.status_name"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        class="px-8 pt-4"
        v-if="showPaymentOptions"
      >
        <div class="flex flex-col gap-2">
          <span class="text-sm text-gray-800 font-bold">Select payment amount</span>
          <div class="flex items-start gap-2">
            <input
              type="radio"
              v-model="paymentAmountSelection"
              value="full_amount"
              class="mt-1"
            />
            <div class="flex flex-col">
              <span class="text-base text-gray-800">{{ maxPaymentAmountFormatted }}</span>
              <span class="text-xs text-gray-400">Full payment amount</span>
            </div>
          </div>
          <hr class="my-2" />
          <div class="flex items-start gap-2 pb-2">
            <input
              type="radio"
              v-model="paymentAmountSelection"
              value="other_amount"
              class="mt-1"
            />
            <div class="flex flex-col sm:flex-row gap-2 md:items-center w-full justify-start">
              <div class="flex flex-col">
                <span class="text-base text-gray-800">Other amount</span>
                <span class="text-xs text-gray-400"
                  >Minimum payment of {{ minimumPaymentAmountFormatted }} allowed</span
                >
              </div>
              <currency-input
                :currency="invoice.currency"
                class="input my-auto max-w-40 ml-0 sm:ml-2"
                :class="{ 'border-red-500': amountBelowMinPaymentAmount }"
                @focus="otherAmountFocused = true"
                @blur="otherAmountFocused = false"
                :options="{
                  currency: invoice.currency,
                  valueRange: { min: 0, max: maxPaymentAmount },
                }"
                v-model="otherAmount"
                v-if="paymentAmountSelection === 'other_amount'"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        class="w-full my-3 flex justify-center"
        v-show="rainforestPaymentLoading || updatingInvoice"
      >
        <client-loading-indicator class="text-primary-500 fill-gray-100 w-5 h-5" />
        <inline-spinner v-if="updatingInvoice" />
      </div>
      <div
        class="w-full rounded my-3 px-4"
        v-show="!rainforestPaymentLoading && !updatingInvoice"
      >
        <!-- RAINFOREST PAYMENT PROCESSOR -->
        <portal-rainforest-payment-form
          :invoice="invoice"
          :amount-btn-text="amountBtnText"
          :intent-amount="intentAmount"
          :amt-field-focused="otherAmountFocused"
          :amount-below-min-payment-amount="amountBelowMinPaymentAmount"
          :amount-above-max-payment-amount="amountAboveMaxPaymentAmount"
          :payment-amount-selection="paymentAmountSelection"
          v-if="invoice && invoice.payment_provider.toLowerCase() === 'rainforest'"
          @intent="setIntent"
          @method-changed="$emit('method-changed', $event)"
          @approved="paymentApprovedEvent"
          @loaded="rainforestPaymentLoading = false"
        />

        <!-- STRIPE PAYMENT PROCESSOR -->
        <portal-stripe-payment-form
          :invoice="invoice"
          :amount-btn-text="amountBtnText"
          :intent-amount="intentAmount"
          :amt-field-focused="otherAmountFocused"
          :amount-below-min-payment-amount="amountBelowMinPaymentAmount"
          :amount-above-max-payment-amount="amountAboveMaxPaymentAmount"
          :payment-amount-selection="paymentAmountSelection"
          v-else-if="invoice && invoice.payment_provider.toLowerCase() === 'stripe'"
          @intent="setIntent"
          @method-changed="$emit('method-changed', $event)"
          @approved="paymentApprovedEvent"
          @loaded="rainforestPaymentLoading = false"
        />
      </div>
      <div
        class="px-4 text-xs text-center text-gray-400"
        v-if="intent && selectedPaymentMethod === 'CARD'"
      >
        This includes a processing fee of
        {{ totalProcessingFeeFormatted }}
      </div>
    </template>
  </div>
</template>
<script>
import PortalInvoiceStatusPill from "../invoices/PortalInvoiceStatusPill.vue"
import currencyUtils from "../../../js/helpers/currencyUtils"
import PortalRainforestPaymentForm from "./PortalRainforestPaymentForm.vue"
import PortalCollectPaymentSuccess from "./PortalCollectPaymentSuccess.vue"
import ClientLoadingIndicator from "../../client_auth/components/client-loading-indicator.vue"
import PortalStripePaymentForm from "./PortalStripePaymentForm.vue"
import CurrencyInput from "../../../js/components/ui/inputs/CurrencyInput.vue"
import PortalPayments from "../../repository/PortalPayments"
import InlineSpinner from "../../../js/components/InlineSpinner.vue"

export default {
  name: "PortalCollectPaymentCard",

  components: {
    PortalStripePaymentForm,
    ClientLoadingIndicator,
    PortalCollectPaymentSuccess,
    PortalRainforestPaymentForm,
    PortalInvoiceStatusPill,
    CurrencyInput,
    InlineSpinner,
  },
  props: {
    invoice: { type: Object, default: () => ({}) },
    selectedPaymentMethod: { type: String, default: "" },
  },
  emits: ["method-changed", "intent", "invoice-updated"],

  data() {
    return {
      intent: null,
      rainforestPaymentLoading: true,
      paymentApproved: false,
      PaidStatuses: ["paid", "refunded", "partial refund", "payment processing", "payment in review"],
      company: window.FinancialCents.company || null,
      paymentAmountSelection: "full_amount",
      otherAmount: this.invoice.allow_partial_payments
        ? currencyUtils.convertFromCents(this.invoice.min_partial_payment_amount)
        : null,
      otherAmountFocused: false,
      pollingInterval: null,
      pollingCounter: 0,
      invoiceUpdated: false,
      updatingInvoice: false,
      amountJustPaid: 0,
    }
  },

  computed: {
    amountDue() {
      return this.invoice.outstanding_amount
    },

    showPaymentPaid() {
      // Show loading state while updating invoice
      if (this.updatingInvoice) {
        return false
      }
      // Show success screen after invoice is updated
      if (this.invoiceUpdated) {
        return true
      }
      // Check if invoice status indicates payment is complete
      const statusName = this.invoice?.status_name || ""
      return this.PaidStatuses.includes(statusName.toLowerCase()) || this.paymentApproved
    },

    maxPaymentAmount() {
      return currencyUtils.convertFromCents(this.amountDue, this.invoice.currency)
    },

    maxPaymentAmountFormatted() {
      return currencyUtils.formatCurrency(this.maxPaymentAmount, this.invoice.currency)
    },

    showPaymentOptions() {
      if (this.invoice.allow_partial_payments) {
        return !this.remainingBalanceBelowMinPaymentAmount
      }
      return false
    },

    remainingBalanceBelowMinPaymentAmount() {
      if (this.invoice.allow_partial_payments) {
        return this.amountDue <= this.invoice.min_partial_payment_amount
      }
      return true
    },

    minimumPaymentAmount() {
      if (this.remainingBalanceBelowMinPaymentAmount) {
        return currencyUtils.convertFromCents(this.amountDue, this.invoice.currency)
      }
      return currencyUtils.convertFromCents(this.invoice.min_partial_payment_amount, this.invoice.currency)
    },

    minimumPaymentAmountFormatted() {
      return currencyUtils.formatCurrency(this.minimumPaymentAmount, this.invoice.currency)
    },

    intentAmount() {
      return this.paymentAmountSelection === "full_amount" ? this.maxPaymentAmount : this.otherAmount
    },

    // This is the actual amount the user is paying, depending on their selections, in dollars.
    // This does not include the processing fee.
    amountToPayWithoutProcessingFee() {
      if (this.paymentAmountSelection === "full_amount") {
        return this.maxPaymentAmount
      } else if (this.paymentAmountSelection === "other_amount") {
        return this.otherAmount
      }

      return this.amountDue
    },

    totalProcessingFeeInCents() {
      if (!this.intent) {
        return 0
      }
      return (this.intent.card_processing_fee_amount || 0) + (this.intent.card_processing_fee_tax_amount || 0)
    },

    amountBtnText() {
      if (this.paymentAmountSelection === "full_amount" && this.intent) {
        const processingFeeInDollars = currencyUtils.convertFromCents(this.totalProcessingFeeInCents)
        const amountPlusProcessingFee = currencyUtils.addAmounts(this.maxPaymentAmount, processingFeeInDollars)

        return `Pay ${currencyUtils.formatCurrency(amountPlusProcessingFee, this.invoice.currency)}`
      } else if (this.paymentAmountSelection === "other_amount" && this.intent) {
        const processingFeeInDollars = currencyUtils.convertFromCents(this.totalProcessingFeeInCents)
        const amountPlusProcessingFee = currencyUtils.addAmounts(this.otherAmount, processingFeeInDollars)

        return `Pay ${currencyUtils.formatCurrency(amountPlusProcessingFee, this.invoice.currency)}`
      }
      return `Pay ${this.amountToPayWithoutProcessingFee}`
    },

    companyName() {
      return this.company?.name
    },

    amountBelowMinPaymentAmount() {
      if (this.paymentAmountSelection === "other_amount" && this.invoice.allow_partial_payments) {
        return this.otherAmount < this.minimumPaymentAmount
      }
      return false
    },

    amountAboveMaxPaymentAmount() {
      if (this.paymentAmountSelection === "other_amount" && this.invoice.allow_partial_payments) {
        return this.otherAmount > this.maxPaymentAmount
      }
      return false
    },

    totalProcessingFeeFormatted() {
      if (!this.intent) {
        return ""
      }
      return this.formatAmount(this.totalProcessingFeeInCents, this.invoice.currency)
    },
  },

  beforeUnmount() {
    // Clear polling interval to prevent memory leaks
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  },

  methods: {
    formatAmount(amount, currency) {
      return currencyUtils.convertAndFormatCurrency(amount, currency)
    },

    async getUpdatedInvoice() {
      const payload = {
        payment_token: this.$route.query.payment_token,
        payin_config_id: this.intent.payin_config_id,
      }
      const { data } = await PortalPayments.getUpdatedInvoice({ invoice: this.invoice, ...payload })

      // If data.data is null, the payment isn't ready yet
      if (data.data === null) {
        return false // Not ready, continue polling
      }

      // Payment is ready, start update process
      this.updatingInvoice = true

      // Emit update to parent
      this.$emit("invoice-updated", data.data)

      // Mark as updated after emission
      this.invoiceUpdated = true
      this.updatingInvoice = false

      return true // Ready, can stop polling
    },

    paymentApprovedEvent(evt) {
      if (evt?.detail[0].status === "SUCCESS") {
        this.paymentApproved = true
        this.amountJustPaid = this.amountToPayWithoutProcessingFee

        if (this.invoice.allow_partial_payments) {
          // for partial payment invoices, we need to poll for updates until we get an updated invoice
          this.startPollingForInvoiceUpdates()
        }
      }
    },

    setIntent(intent) {
      this.intent = intent
      this.$emit("intent", intent)
    },

    startPollingForInvoiceUpdates() {
      // Clear any existing polling
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
      }

      this.pollingCounter = 0

      this.pollingInterval = setInterval(async () => {
        try {
          this.pollingCounter++

          // Stop polling after 100 attempts
          if (this.pollingCounter >= 100) {
            clearInterval(this.pollingInterval)
            this.pollingInterval = null
            return
          }

          const isReady = await this.getUpdatedInvoice()

          // If the API returned data (not null), payment is ready - stop polling
          if (isReady) {
            clearInterval(this.pollingInterval)
            this.pollingInterval = null
            return
          }

          // If data.data was null, continue polling until max attempts
        } catch (error) {
          console.error("Error polling for invoice updates:", error)
          // Continue polling even if there's an error
        }
      }, 1000) // Poll every 1 second
    },

    makeAnotherPayment() {
      // Reset flags to show payment form again
      this.paymentAmountSelection = "full_amount"
      this.invoiceUpdated = false
      this.paymentApproved = false
      this.updatingInvoice = false
      this.amountJustPaid = 0
      this.otherAmount = this.minimumPaymentAmount
    },
  },
}
</script>
